/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState } from "react"
import tw from "twin.macro"
import { useTranslation } from "next-i18next"
import {
  Platform,
  ProfileOnGetProfileFragment,
  QuestionDetailFragment,
  useExportFanSummaryMutation,
  useGetFanSummaryLazyQuery,
  useGetFanSummaryQuery,
  useGetTopQuestionsLazyQuery,
  useGetTopQuestionsQuery,
  UserOnGetUserFragmentFragment,
} from "../../graphql/generated/graphql"
import { useEffectAsync } from "../../utils/hooks/useEffectAsync"
import { ComponentLoading } from "../../components/loading"
import { Button } from "../../components/formElements/button" // Assuming you have a Button component

import { TopQuestionCard } from "./cards"
import { QuestionTableCell, TextTableCell, TableHeaderCell, UserTableCell, IconTableCell } from "./cell"

import { ReactComponent as DiscordIcon } from "../../assets/icons/discord.svg"
import { ReactComponent as YouTubeIcon } from "../../assets/icons/youTube.svg"
import { ReactComponent as InstagramIcon } from "../../assets/icons/instagram.svg"
import { ReactComponent as BonsaiIcon } from "../../assets/icons/bonsai.svg"
import { downloadFileUrl } from "../../components/share/shareContent"
import { IconLink } from "./iconLink"

export type FanData = {
  questions: number
  answers: number
  user: ProfileOnGetProfileFragment
  topQuestion: QuestionDetailFragment | null
  platform: Platform[]
}

export function getIconForPlatform(platform: Platform, key: string) {
  switch (platform) {
    case Platform.Discord:
      return <DiscordIcon key={key} />
    case Platform.Youtube:
      return <YouTubeIcon key={key} />
    case Platform.Instagram:
      return <InstagramIcon key={key} />
    default:
    case Platform.WebClient:
      return <BonsaiIcon key={key} />
  }
}

export function getIconLinkForPlatform(platform: Platform, key: string, userProfile: ProfileOnGetProfileFragment) {
  switch (platform) {
    case Platform.Discord:
      const discordUrl = `https://discord.com/users/${userProfile.discordUserId}`
      return <IconLink key={key} icon={<DiscordIcon />} url={discordUrl} isExternal={true} />
    case Platform.Youtube:
      const youtubeUrl = `https://www.youtube.com/${userProfile.youTubeHandle}`
      return <IconLink key={key} icon={<YouTubeIcon />} url={youtubeUrl} isExternal={true} />
    case Platform.Instagram:
      const instagramUrl = `https://www.instagram.com/${userProfile.instagramUsername}`
      return <IconLink key={key} icon={<InstagramIcon />} url={instagramUrl} isExternal={true} />
    default:
    case Platform.WebClient:
      const bonsaiUrl = `/${userProfile.username}`
      return <IconLink key={key} icon={<BonsaiIcon />} url={bonsaiUrl} isExternal={false} />
  }
}

export const FanSummary = () => {
  const { t } = useTranslation("fan")

  const [fanPage, setFanPage] = useState<number>(0)
  const [allFans, setAllFans] = useState<FanData[]>([])

  const { data: fanSummaryData, loading: isFanSummaryLoading } = useGetFanSummaryQuery({
    variables: {
      limit: 10,
      page: 0,
    },
  })

  const { data: topQuestionsData, loading: isLoadingTopQuestions } = useGetTopQuestionsQuery({
    variables: {
      page: 1,
      limit: 20,
    },
  })
  const [getFanSummaryQuery, { loading: isLoadingMore }] = useGetFanSummaryLazyQuery()
  const [exportFanSummary] = useExportFanSummaryMutation()
  const [exportFanData, setExportFanData] = useState<boolean>(false)
  const [hasMore, setHasMore] = useState<boolean>(false)
  const [isExporting, setIsExporting] = useState(false)

  useEffectAsync(async () => {
    if (exportFanData) {
      const fanDataLocation = await exportFanSummary()
      setExportFanData(false)
      const s3url = fanDataLocation.data?.ExportFanSummary?.s3url
      s3url && (await downloadFileUrl(s3url, "bonsai-fan-data.csv"))
    }
  }, [exportFanData])

  useEffectAsync(async () => {
    if (fanSummaryData?.FanSummary?.fans) {
      setAllFans(fanSummaryData.FanSummary.fans as FanData[])
      setHasMore(fanSummaryData.FanSummary.pagination.totalCount > fanSummaryData.FanSummary.fans.length)
    }
  }, [fanSummaryData])

  const loadMoreFans = async () => {
    const nextPage = fanPage + 1
    const newFanSummaryPageData = await getFanSummaryQuery({
      variables: {
        limit: 10,
        page: nextPage,
      },
    })

    if (newFanSummaryPageData.data && newFanSummaryPageData.data.FanSummary) {
      const newFans = newFanSummaryPageData.data.FanSummary.fans as FanData[]
      setAllFans((prevFans) => [...prevFans, ...newFans])
      setFanPage(nextPage)
      setHasMore(newFanSummaryPageData.data.FanSummary.pagination.totalCount > newFans.length)
    }
  }

  const handleExport = async () => {
    setIsExporting(true)
    try {
      const fanDataLocation = await exportFanSummary()
      const s3url = fanDataLocation.data?.ExportFanSummary?.s3url

      if (s3url) {
        await downloadFileUrl(s3url, "bonsai-fan-data.csv")
      }
    } catch (error) {
      console.error("Export failed:", error)
    } finally {
      setIsExporting(false)
    }
  }

  const topQuestions = topQuestionsData?.TopFanQuestions?.topFanQuestions
  const fans = fanSummaryData?.FanSummary?.fans

  const topQuestionCards = topQuestions?.map((q, index) => {
    const fan = fans && (fans.find((fan) => fan.user.id === q.Fan.id) as FanData | undefined)
    const isFirst = index === 0
    const isLast = index === topQuestions.length - 1
    return fan && <TopQuestionCard key={q.id} question={q} fanData={fan} isFirst={isFirst} isLast={isLast} />
  })

  const tableRows = allFans.map((fan) => {
    const fanIconLinks = fan.platform.map((platform, index) => {
      return getIconLinkForPlatform(platform, index.toString(), fan.user)
    })

    return (
      <div key={fan.user.id} tw="flex flex-col space-y-2">
        <div tw="flex flex-row flex-nowrap border-b-2 border-system-gray-1">
          <UserTableCell user={fan.user} />
          <TextTableCell text={`${fan.questions}`} />
          <TextTableCell text={`${fan.answers}`} />
          <IconTableCell iconLinks={fanIconLinks} />
          <QuestionTableCell question={fan.topQuestion} />
        </div>
      </div>
    )
  })

  return (
    <div tw="flex flex-col justify-center space-y-8">
      <div tw="flex flex-col">
        <div tw="flex flex-row items-center mt-2">
          <span tw="font-bold text-lg">{t("topFansTitle")}</span>
        </div>
        <div tw="flex flex-row pt-4 overflow-x-auto">
          {isLoadingTopQuestions ? <ComponentLoading /> : topQuestionCards}
        </div>
      </div>

      {/* fan table starts here  */}
      <div tw="flex flex-col justify-center space-y-4">
        <div tw="flex flex-row items-center">
          <p tw="font-bold text-lg flex-none">{t("fanTableTitle")}</p>
          <span
            tw="ml-auto underline cursor-pointer font-light text-sm align-middle"
            css={[isExporting && tw`cursor-wait opacity-50`, !isExporting && tw`hover:text-system-gray-4`]}
            onClick={isExporting ? undefined : handleExport}
          >
            {t("exportDataLabel")}
          </span>
        </div>
        <div tw="flex flex-row flex-nowrap border-b-2 border-system-gray-3">
          <TableHeaderCell text="Username" />
          <TableHeaderCell text="Questions" />
          <TableHeaderCell text="Answers" />
          <TableHeaderCell text="Socials" />
          <TableHeaderCell text="Open Questions" />
        </div>
        {isFanSummaryLoading || isLoadingMore ? (
          <div tw="my-20">
            <ComponentLoading />
          </div>
        ) : (
          tableRows
        )}
        {allFans.length > 0 && hasMore && (
          <div tw="flex justify-center mt-4">
            <Button
              onClick={loadMoreFans}
              isDisabled={isLoadingMore}
              action={isLoadingMore ? "Loading..." : "Load More"}
            />
          </div>
        )}
      </div>
    </div>
  )
}
